import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useGetOrgContractDocumentById } from '@/graphql';
import { useAuthStore } from '@/state/store/auth';
import type { LegalDocument } from '@/schemas/schema';
import { useActiveOrganization } from '@/graphql/queries/organizations';
import { UploadAndSetup, type UploadAndSetupSubmitArgs } from '../components/upload-and-setup';
import { LoadingOverlay } from '../components/loading-overlay';
import { documentToText } from '../lib/document-to-text';
import { extractPdfText } from '../lib/extract-pdf-text';
import { isFixedDocxContract } from '@/modules/org/contracts/utils';
import { getContractPdfBytes } from '@/modules/templates/services/docx-convert.service';
import { analyzeContract } from '../services/contract-analysis-ai.service';
import { useCreateContractAnalysis } from '../hooks/use-contract-analyses';
import type { ContractRole } from '../types/contract-analysis.types';

/**
 * Org route: `/org/<slug>/contract-checker/new`
 *
 * Wraps `<UploadAndSetup>` with the submission orchestration:
 *   1. Resolve the contract text (PDF extraction or prefilled from
 *      another contract's stored `Document` JSON).
 *   2. Run the AI analysis (synchronous SSE call, ~20–40s).
 *   3. Persist to UDS.
 *   4. Navigate to `/contract-checker/result/<itemId>`.
 *
 * If the user arrives with `?contractId=<id>`, we fetch that contract
 * and pass its title + parsed document text down as `prefill`.
 */
const OrgContractCheckerNewPage = () => {
  const { t } = useTranslation();
  const { orgId: slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { orgUDSId, sigOrgId } = useAuthStore();
  const { data: org, isLoading: isOrgLoading } = useActiveOrganization();

  const contractId = searchParams.get('contractId') ?? '';
  const { data: sourceContract, isLoading: isSourceLoading } = useGetOrgContractDocumentById(
    contractId,
    Boolean(contractId)
  );

  const createMutation = useCreateContractAnalysis();
  // Tracks the WHOLE pipeline (text extraction + AI call + UDS insert).
  // `createMutation.isPending` alone only covers the final insert and
  // would leave the user staring at a static form during the 20–40s AI
  // call.
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const isSubmitting = isAnalyzing || createMutation.isPending;

  const prefill = useMemo(() => {
    if (!contractId || !sourceContract) return undefined;
    return {
      contractId,
      contractTitle: sourceContract.Name || sourceContract.TemplateTitle || 'Contract',
    };
  }, [contractId, sourceContract]);

  const resolveContractText = useCallback(
    async (
      file: File | null
    ): Promise<{ text: string; projectName: string; sourceFileName?: string }> => {
      if (contractId && sourceContract) {
        // Fixed-DOCX contracts store a PDF reference, not a LegalDocument — pull
        // the text out of the generated PDF with the same extractor the upload
        // path uses.
        const fixedDocx = isFixedDocxContract(sourceContract.Document);
        if (fixedDocx?.docxUrl || fixedDocx?.url) {
          // Derive the PDF from the stored .docx so the analysis always reflects
          // the current text; `Document.url` is a legacy fallback for records
          // created before PDFs stopped being stored (it goes stale on edit).
          let blob: Blob;
          if (fixedDocx.docxUrl) {
            const bytes = await getContractPdfBytes({
              docxUrl: fixedDocx.docxUrl,
              docxFileId: fixedDocx.docxFileId,
              fileName: `${sourceContract.TemplateSlug || 'contract'}.docx`,
            });
            blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
          } else {
            const res = await fetch(fixedDocx.url as string);
            if (!res.ok) {
              throw new Error('Could not load the contract PDF');
            }
            blob = await res.blob();
          }
          const pdfFile = new File([blob], fixedDocx.fileName || 'contract.pdf', {
            type: 'application/pdf',
          });
          const text = await extractPdfText(pdfFile);
          if (!text.trim()) {
            throw new Error('Could not extract text from the contract PDF');
          }
          return {
            text,
            projectName: sourceContract.Name || sourceContract.TemplateTitle || 'Contract',
            sourceFileName: fixedDocx.fileName,
          };
        }

        let parsedDoc: LegalDocument | null = null;
        if (sourceContract.Document) {
          try {
            parsedDoc = JSON.parse(sourceContract.Document) as LegalDocument;
          } catch (err) {
            console.error('[contract-checker] Failed to parse source contract Document', err);
          }
        }
        if (!parsedDoc) {
          throw new Error('Source contract has no readable document content');
        }
        return {
          text: documentToText(parsedDoc),
          projectName: sourceContract.Name || sourceContract.TemplateTitle || 'Contract',
        };
      }

      if (!file) throw new Error('No file provided');
      const text = await extractPdfText(file);
      if (!text.trim()) {
        throw new Error('Could not extract text from the uploaded PDF');
      }
      const projectName = file.name.replace(/\.[^.]+$/, '').trim() || 'Untitled contract';
      return { text, projectName, sourceFileName: file.name };
    },
    [contractId, sourceContract]
  );

  const handleSubmit = useCallback(
    async ({ file, role, topics }: UploadAndSetupSubmitArgs) => {
      if (!orgUDSId || !slug) return;
      setIsAnalyzing(true);
      try {
        const { text, projectName, sourceFileName } = await resolveContractText(file);

        const analysis = await analyzeContract({
          contractText: text,
          role: role as ContractRole,
          topics: topics.trim() || undefined,
          projectName,
        });

        const inserted = await createMutation.mutateAsync({
          OrganizationId: orgUDSId,
          SigOrgId: sigOrgId ?? undefined,
          ProjectName: analysis.projectName,
          Role: role as ContractRole,
          Topics: topics.trim() || undefined,
          SourceFileName: sourceFileName,
          SourceContractId: contractId || undefined,
          SourceContractTitle:
            contractId && sourceContract
              ? sourceContract.Name || sourceContract.TemplateTitle
              : undefined,
          TotalPoints: analysis.totalPoints,
          HighRiskCount: analysis.highRiskCount,
          MediumRiskCount: analysis.mediumRiskCount,
          LowRiskCount: analysis.lowRiskCount,
          Findings: JSON.stringify(analysis.findings),
          MatchedFindingIds: JSON.stringify(analysis.matchedIds),
        });

        const itemId = inserted.insertOrgContractAnalysis.itemId;
        navigate(`/org/${slug}/contract-checker/result/${itemId}`, { replace: true });
      } catch (error) {
        console.error('[contract-checker] analysis failed', error);
        toast({
          variant: 'destructive',
          title: t('ORGCONTRACTCHECKER_ANALYZE_ERROR', {
            defaultValue: 'Could not analyze the contract. Please try again.',
          }),
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setIsAnalyzing(false);
      }
    },
    [
      contractId,
      createMutation,
      navigate,
      orgUDSId,
      sigOrgId,
      resolveContractText,
      slug,
      sourceContract,
      t,
      toast,
    ]
  );

  if (isOrgLoading || (contractId && isSourceLoading)) {
    return <Skeleton className="h-150 w-full" />;
  }
  if (!org || !slug) return null;

  if (isSubmitting) return <LoadingOverlay />;

  return (
    <UploadAndSetup
      prefill={prefill}
      changeHref={`/org/${slug}/contract-checker/new`}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  );
};

export default OrgContractCheckerNewPage;
